import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from '../services/api';
import toast from 'react-hot-toast';
import FinanceLockGate, { FinanceLockSettingsPanel } from '../components/FinanceLockGate';
import useChairPayment from '../hooks/useChairPayment';
import {
  Activity,
  Building2,
  Calculator,
  CheckCircle2,
  IndianRupee,
  LockKeyhole,
  Rocket,
  Stethoscope,
  CreditCard,
  UserPlus,
} from 'lucide-react';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const IST_TIME_ZONE = 'Asia/Kolkata';
const PHONE_REGEX = /^[6-9]\d{9}$/;
const UPI_REGEX = /^[\w.\-]+@[\w.\-]+$/;
const GST_REGEX = /^[0-9A-Z]{15}$/;
const formatInrFromPaise = (paise) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
}).format(Math.max(0, Number(paise) || 0) / 100);
const SETUP_FORM_DEFAULTS = {
  clinicLegalName: '',
  leadDoctor: '',
  licenseNumber: '',
  mobileWhatsapp: '',
  upiId: '',
  clinicAddress: '',
  clinicLogo: '',
  numberOfDentalChairs: 1,
  gstNumber: '',
  openingTime: '09:00',
  breakStartTime: '',
  breakEndTime: '',
  closingTime: '21:00',
  bufferMinutes: 5,
  setupInvestment: '',
  patientVisitsPerMonth: '',
  averageAppointmentDuration: 30,
  rent: '',
  electricityBill: '',
  waterBill: '',
  miscellaneous: '',
  staffSalary: '',
  doctorSalary: '',
  assistantSalary: '',
  yourOwnSalary: '',
  softwares: '',
  onlineMarketing: '',
  offlineMarketing: '',
  equipmentValue: '',
  equipmentLifeYears: 5,
  gstRate: 0,
  incomeTaxRate: 25,
  interest: '',
};

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getIstDateParts(value = new Date()) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date(value));
  const map = {};

  parts.forEach((part) => {
    if (part.type !== 'literal') {
      map[part.type] = part.value;
    }
  });

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
  };
}

function formatIstDateKey(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const { year, month, day } = getIstDateParts(date);
  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-');
}

function calculateDaysOpen(weeklyHolidayList, customHolidayList, referenceDate = new Date()) {
  const { year, month } = getIstDateParts(referenceDate);
  const totalDays = new Date(year, month, 0).getDate();
  const weeklyHolidaySet = new Set(Array.isArray(weeklyHolidayList) ? weeklyHolidayList : []);
  const customHolidaySet = new Set(
    (Array.isArray(customHolidayList) ? customHolidayList : [])
      .map((item) => formatIstDateKey(item))
      .filter(Boolean)
  );

  let holidaysCount = 0;

  for (let day = 1; day <= totalDays; day += 1) {
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const dayName = date.toLocaleDateString('en-US', {
      weekday: 'long',
      timeZone: IST_TIME_ZONE,
    });
    const dateKey = [
      String(year).padStart(4, '0'),
      String(month).padStart(2, '0'),
      String(day).padStart(2, '0'),
    ].join('-');

    if (weeklyHolidaySet.has(dayName) || customHolidaySet.has(dateKey)) {
      holidaysCount += 1;
    }
  }

  return Math.max(0, totalDays - holidaysCount);
}

function calculateHoursOpen(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  const [startHours, startMinutes] = String(startStr).split(':').map(Number);
  const [endHours, endMinutes] = String(endStr).split(':').map(Number);

  const startDecimal =
    (Number.isFinite(startHours) ? startHours : 0) + (Number.isFinite(startMinutes) ? startMinutes : 0) / 60;
  const endDecimal =
    (Number.isFinite(endHours) ? endHours : 0) + (Number.isFinite(endMinutes) ? endMinutes : 0) / 60;

  return Math.max(0, endDecimal - startDecimal);
}

function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [hours, minutes] = String(timeStr).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

function formatMinutesAsHours(minutes) {
  const safeMinutes = Math.max(0, Math.round(toNumber(minutes)));
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  return `${hours} hr${hours === 1 ? '' : 's'} ${remainder} min`;
}

function calculateDailyWorkingMinutes(openingTime, breakStartTime, breakEndTime, closingTime) {
  const opening = parseTimeToMinutes(openingTime);
  const closing = parseTimeToMinutes(closingTime);

  if (opening === null || closing === null || closing <= opening) {
    return {
      totalOpenMinutes: 0,
      breakMinutes: 0,
      workingMinutes: 0,
      isBreakComplete: false,
      isBreakValid: true,
    };
  }

  const hasBreakStart = hasTrimmedValue(breakStartTime);
  const hasBreakEnd = hasTrimmedValue(breakEndTime);
  let breakMinutes = 0;
  let isBreakValid = true;

  if (hasBreakStart || hasBreakEnd) {
    const breakStart = parseTimeToMinutes(breakStartTime);
    const breakEnd = parseTimeToMinutes(breakEndTime);
    isBreakValid =
      breakStart !== null &&
      breakEnd !== null &&
      breakStart > opening &&
      breakEnd > breakStart &&
      breakEnd < closing;

    if (isBreakValid) {
      breakMinutes = breakEnd - breakStart;
    }
  }

  const totalOpenMinutes = closing - opening;

  return {
    totalOpenMinutes,
    breakMinutes,
    workingMinutes: Math.max(0, totalOpenMinutes - breakMinutes),
    isBreakComplete: hasBreakStart && hasBreakEnd,
    isBreakValid,
  };
}

function hasTrimmedValue(value) {
  return String(value ?? '').trim() !== '';
}

function getInputClass(hasError) {
  return hasError
    ? 'input-modern border-rose-300 text-rose-900 placeholder:text-rose-300 focus:border-rose-500 focus:ring-rose-100'
    : 'input-modern';
}

function getFirstErrorMessage(formErrors = {}) {
  for (const error of Object.values(formErrors)) {
    if (typeof error === 'string' && error) {
      return error;
    }

    if (error?.message) {
      return error.message;
    }
  }

  return 'Please fix the highlighted setup fields and try again.';
}

function clampNumber(value, min, max, fallback = min) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function formatCurrency(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  })}`;
}

function FieldShell({ label, error, children, helper }) {
  return (
    <div>
      <label className="label-modern">{label}</label>
      {children}
      {helper ? <p className="mt-2 text-xs leading-5 text-slate-500">{helper}</p> : null}
      {error ? <p className="mt-2 text-xs font-semibold text-rose-600">{error.message}</p> : null}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="mb-6 flex items-start gap-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50/80 text-blue-700 ring-1 ring-blue-100">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-blue-700">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
      </div>
    </div>
  );
}

function SliderNumberField({
  label,
  field,
  value,
  register,
  setValue,
  error,
  min,
  max,
  step = 1,
  placeholder,
  helper,
  rules,
}) {
  const displayValue = value === undefined || value === null || value === '' ? '' : value;
  const rangeValue = clampNumber(displayValue, min, max, min);

  return (
    <FieldShell label={label} error={error} helper={helper}>
      <div className="rounded-2xl border border-slate-200 bg-white p-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={rangeValue}
          onChange={(event) => setValue(field, event.target.value, { shouldDirty: true, shouldValidate: true })}
          className="h-1.5 w-full cursor-pointer accent-blue-600"
        />
        <div className="mt-3 flex items-center gap-3">
          <input
            {...register(field, rules)}
            type="number"
            min={min}
            max={max}
            step={step}
            className={getInputClass(Boolean(error))}
            placeholder={placeholder}
          />
          <span className="min-w-[72px] rounded-xl bg-slate-50 px-3 py-2 text-center text-sm font-semibold text-slate-700">
            {displayValue || rangeValue}
          </span>
        </div>
      </div>
    </FieldShell>
  );
}

function ReadOnlyRange({ label, value, min, max, suffix = '', helper }) {
  const percent = max > min ? ((clampNumber(value, min, max, min) - min) / (max - min)) * 100 : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-700">{label}</p>
        <p className="text-sm font-semibold text-blue-700">
          {Number(value || 0).toFixed(label.includes('Hours') ? 2 : 0)}
          {suffix}
        </p>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-blue-600" style={{ width: `${percent}%` }} />
      </div>
      {helper ? <p className="mt-2 text-xs leading-5 text-slate-500">{helper}</p> : null}
    </div>
  );
}

export default function Setup() {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, submitCount },
  } = useForm({
    defaultValues: SETUP_FORM_DEFAULTS,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [inkpadBackground, setInkpadBackground] = useState({ exists: false });
  const [inkpadBackgroundPreview, setInkpadBackgroundPreview] = useState('');
  const [inkpadBackgroundBusy, setInkpadBackgroundBusy] = useState(false);
  const [weeklyHolidays, setWeeklyHolidays] = useState([]);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState('');
  const [financeReady, setFinanceReady] = useState(false);
  const [activeSetupStep, setActiveSetupStep] = useState('clinic');
  const [chairPricing, setChairPricing] = useState(null);
  const [chairSummary, setChairSummary] = useState(null);
  const [chairPricingLoading, setChairPricingLoading] = useState(false);
  const [chairPricingError, setChairPricingError] = useState('');
  const [chairUnlockTab, setChairUnlockTab] = useState('payment');
  const [monthlyPerformance, setMonthlyPerformance] = useState(null);
  const [monthlyPerformanceLoading, setMonthlyPerformanceLoading] = useState(false);
  const [monthlyReviewOpen, setMonthlyReviewOpen] = useState(false);
  const [monthlySelections, setMonthlySelections] = useState({ patientVisits: true, averageAppointmentDuration: true, treatmentIds: [] });
  const sectionRefs = useRef({});

  const navigate = useNavigate();

  useEffect(() => {
    if (financeReady) {
      fetchSetup();
      fetchChairChargeability();
      fetchMonthlyPerformance();
    }
  }, [financeReady]);

  const fetchChairChargeability = async () => {
    setChairPricingLoading(true);
    setChairPricingError('');
    try {
      const [pricingResponse, summaryResponse] = await Promise.all([
        axios.get('/addons/chairs/pricing'),
        axios.get('/addons/chairs/summary'),
      ]);
      setChairPricing(pricingResponse.data);
      setChairSummary(summaryResponse.data);
    } catch (error) {
      setChairPricingError(error.response?.data?.error || 'Pricing unavailable. Please contact Dektal support.');
    } finally {
      setChairPricingLoading(false);
    }
  };

  const fetchMonthlyPerformance = async () => {
    setMonthlyPerformanceLoading(true);
    try {
      const response = await axios.get('/treatment-timers/monthly-duration-intelligence');
      const report = response.data || null;
      setMonthlyPerformance(report);
      setMonthlySelections({
        patientVisits: true,
        averageAppointmentDuration: Boolean(report?.averageAppointmentDuration),
        treatmentIds: (report?.treatments || []).filter((row) => row.eligibleForUpdate).map((row) => row.treatmentId),
      });
    } catch (error) {
      if (![403, 423].includes(error.response?.status)) {
        console.error('Failed to load monthly clinic performance:', error);
      }
    } finally {
      setMonthlyPerformanceLoading(false);
    }
  };

  const keepMonthlyValues = async () => {
    try {
      await axios.post('/treatment-timers/monthly-duration-intelligence/keep-current');
      setMonthlyPerformance((current) => current ? { ...current, promptAvailable: false, status: 'kept_current' } : current);
      setMonthlyReviewOpen(false);
      toast.success('Current Capacity Assumptions were kept.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not save your choice.');
    }
  };

  const applyMonthlyValues = async () => {
    setMonthlyPerformanceLoading(true);
    try {
      await axios.post('/treatment-timers/monthly-duration-intelligence/apply', { selections: monthlySelections });
      setMonthlyReviewOpen(false);
      toast.success('Selected actual clinic values were applied. Selling Prices were preserved.');
      await Promise.all([fetchSetup(), fetchMonthlyPerformance()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not apply the monthly update.');
    } finally {
      setMonthlyPerformanceLoading(false);
    }
  };

  const {
    startChairPayment,
    isProcessing: chairPaymentProcessing,
    paymentError: chairPaymentError,
    receipt: chairPaymentReceipt,
  } = useChairPayment({ onVerified: fetchChairChargeability });

  useEffect(() => {
    if (initializing) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];

        const nextStep = visibleEntry?.target?.dataset?.setupStep;
        if (nextStep) {
          setActiveSetupStep(nextStep);
        }
      },
      {
        root: null,
        rootMargin: '-22% 0px -58% 0px',
        threshold: [0.08, 0.18, 0.32, 0.5],
      }
    );

    Object.values(sectionRefs.current).forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, [initializing]);

  const applySetupData = (data = {}) => {
    const nextValues = { ...SETUP_FORM_DEFAULTS };

    Object.keys(SETUP_FORM_DEFAULTS).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        nextValues[key] = data[key];
      }
    });

    reset(nextValues);
    setWeeklyHolidays(Array.isArray(data.weeklyHolidays) ? data.weeklyHolidays : []);
    setCustomHolidays(Array.isArray(data.customHolidays) ? data.customHolidays : []);
    setLogoPreview(typeof data.clinicLogo === 'string' ? data.clinicLogo : '');
    if (data.chairSummary) setChairSummary(data.chairSummary);
  };

  const fetchSetup = async () => {
    setInitializing(true);
    setLoadError('');
    try {
      const [res, backgroundRes] = await Promise.all([
        axios.get('/setup'),
        axios.get('/setup/inkpad-background'),
      ]);
      applySetupData(res.data || {});
      setInkpadBackground(backgroundRes.data || { exists: false });
      if (backgroundRes.data?.exists) {
        const previewRes = await axios.get('/setup/inkpad-background/content', { responseType: 'blob' });
        setInkpadBackgroundPreview((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return URL.createObjectURL(previewRes.data);
        });
      } else {
        setInkpadBackgroundPreview((previous) => { if (previous) URL.revokeObjectURL(previous); return ''; });
      }
    } catch (err) {
      console.error('Failed to load setup:', err);
      const message = err.response?.data?.error || err.message || 'Failed to load setup';
      setLoadError(message);
      toast.error(`Failed to load setup: ${message}`);
    } finally {
      setInitializing(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file only');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
      setValue('clinicLogo', reader.result, { shouldDirty: true });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview('');
    setValue('clinicLogo', '', { shouldDirty: true });
  };

  const uploadInkpadBackground = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { toast.error('Prescription background must be a JPG or PNG image'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Prescription background must be 8 MB or smaller'); return; }
    const formData = new FormData();
    formData.append('background', file);
    setInkpadBackgroundBusy(true);
    try {
      const response = await axios.post('/setup/inkpad-background', formData);
      const previewResponse = await axios.get('/setup/inkpad-background/content', { responseType: 'blob' });
      setInkpadBackground(response.data);
      setInkpadBackgroundPreview((previous) => { if (previous) URL.revokeObjectURL(previous); return URL.createObjectURL(previewResponse.data); });
      toast.success(inkpadBackground.exists ? 'InkPad prescription background replaced.' : 'InkPad prescription background uploaded.');
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to upload InkPad prescription background'); }
    finally { setInkpadBackgroundBusy(false); }
  };

  const removeInkpadBackground = async () => {
    if (!window.confirm('Remove the Dektal InkPad prescription background? Existing InkPad handwriting will not be deleted.')) return;
    setInkpadBackgroundBusy(true);
    try {
      await axios.delete('/setup/inkpad-background');
      setInkpadBackground({ exists: false });
      setInkpadBackgroundPreview((previous) => { if (previous) URL.revokeObjectURL(previous); return ''; });
      toast.success('InkPad prescription background removed.');
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to remove InkPad prescription background'); }
    finally { setInkpadBackgroundBusy(false); }
  };

  const onSubmit = async (data) => {
    if (initializing) {
      toast.error('Saved setup is still loading. Please wait a moment and try again.');
      return;
    }

    setLoading(true);
    setSaveError('');

    try {
      const normalizedLicenseNumber = String(data.licenseNumber || '').trim();
      const normalizedPhone = String(data.mobileWhatsapp || '').replace(/\D/g, '').slice(-10);
      const normalizedUpiId = String(data.upiId || '').trim();
      const normalizedGstNumber = String(data.gstNumber || '').trim().toUpperCase();
      const chairCount = Math.trunc(toNumber(data.numberOfDentalChairs, 0));
      const bufferMinutes = Math.trunc(toNumber(data.bufferMinutes, 5));
      const averageAppointmentDuration = Math.trunc(toNumber(data.averageAppointmentDuration, 30));

      const submitValidationErrors = {};

      if (!hasTrimmedValue(data.clinicLegalName)) {
        submitValidationErrors.clinicLegalName = 'Clinic Legal Name is required.';
      }

      if (!hasTrimmedValue(data.leadDoctor)) {
        submitValidationErrors.leadDoctor = 'Lead Doctor Name is required.';
      }

      if (!normalizedLicenseNumber) {
        submitValidationErrors.licenseNumber = 'License Number is required.';
      }

      if (!normalizedPhone) {
        submitValidationErrors.mobileWhatsapp = 'Mobile (WhatsApp) is required.';
      }

      if (!data.openingTime) {
        submitValidationErrors.openingTime = 'Opening Time is required.';
      }

      if (!data.closingTime) {
        submitValidationErrors.closingTime = 'Closing Time is required.';
      }

      const workingTimeValidation = calculateDailyWorkingMinutes(
        data.openingTime,
        data.breakStartTime,
        data.breakEndTime,
        data.closingTime
      );

      if (
        (hasTrimmedValue(data.breakStartTime) || hasTrimmedValue(data.breakEndTime)) &&
        !(hasTrimmedValue(data.breakStartTime) && hasTrimmedValue(data.breakEndTime))
      ) {
        submitValidationErrors.breakStartTime = 'Enter both break start and break end, or leave both blank.';
        submitValidationErrors.breakEndTime = 'Enter both break start and break end, or leave both blank.';
      }

      if (!workingTimeValidation.isBreakValid) {
        submitValidationErrors.breakStartTime = 'Break must sit between opening and closing time.';
        submitValidationErrors.breakEndTime = 'Break end must be after break start and before closing time.';
      }

      if (chairCount < 1) {
        submitValidationErrors.numberOfDentalChairs = 'Number of dental chairs must be between 1 and 20.';
      }

      if (Object.keys(submitValidationErrors).length) {
        Object.entries(submitValidationErrors).forEach(([field, message]) => {
          setError(field, { type: 'manual', message });
        });
        toast.error(getFirstErrorMessage(submitValidationErrors));
        setLoading(false);
        return;
      }

      if (normalizedLicenseNumber.length > 100) {
        setError('licenseNumber', {
          type: 'manual',
          message: 'License Number must be 100 characters or fewer.',
        });
        toast.error('License Number must be 100 characters or fewer.');
        setLoading(false);
        return;
      }

      if (!PHONE_REGEX.test(normalizedPhone)) {
        setError('mobileWhatsapp', {
          type: 'manual',
          message: 'Invalid WhatsApp number. Must be 10 digits starting with 6/7/8/9.',
        });
        toast.error('Invalid WhatsApp number. Must be 10 digits starting with 6/7/8/9.');
        setLoading(false);
        return;
      }

      if (normalizedUpiId && !UPI_REGEX.test(normalizedUpiId)) {
        setError('upiId', {
          type: 'manual',
          message: 'Please enter a valid UPI ID (name@bank)',
        });
        toast.error('Please enter a valid UPI ID (name@bank)');
        setLoading(false);
        return;
      }

      if (normalizedGstNumber && !GST_REGEX.test(normalizedGstNumber)) {
        setError('gstNumber', {
          type: 'manual',
          message: 'GST number must be 15 characters.',
        });
        toast.error('GST number must be 15 characters.');
        setLoading(false);
        return;
      }

      if (chairCount < 1 || chairCount > 20) {
        setError('numberOfDentalChairs', {
          type: 'manual',
          message: 'Number of dental chairs must be between 1 and 20.',
        });
        toast.error('Number of dental chairs must be between 1 and 20.');
        setLoading(false);
        return;
      }

      if (bufferMinutes < 0 || bufferMinutes > 240) {
        setError('bufferMinutes', {
          type: 'manual',
          message: 'Buffer between appointments must be between 0 and 240 minutes.',
        });
        toast.error('Buffer between appointments must be between 0 and 240 minutes.');
        setLoading(false);
        return;
      }

      if (averageAppointmentDuration < 1 || averageAppointmentDuration > 480) {
        setError('averageAppointmentDuration', {
          type: 'manual',
          message: 'Average appointment duration must be between 1 minute and 8 hours.',
        });
        toast.error('Average appointment duration must be between 1 minute and 8 hours.');
        setLoading(false);
        return;
      }

      if (workingTimeValidation.workingMinutes <= 0) {
        setError('closingTime', {
          type: 'manual',
          message: 'Net working time must be greater than zero.',
        });
        toast.error('Net working time must be greater than zero.');
        setLoading(false);
        return;
      }

      const payload = {
        ...data,
        licenseNumber: normalizedLicenseNumber,
        mobileWhatsapp: normalizedPhone,
        upiId: normalizedUpiId || null,
        gstNumber: normalizedGstNumber,
        numberOfDentalChairs: chairCount,
        bufferMinutes,
        averageAppointmentDuration,
        weeklyHolidays,
        customHolidays,
        clinicLogo: data.clinicLogo || '',
      };

      const res = await axios.post('/setup', payload);
      applySetupData(res.data || payload);
      toast.success('Setup saved successfully. Opening Dashboard...');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Setup save error:', err);
      const details = err.response?.data?.details;
      if (details && typeof details === 'object') {
        Object.entries(details).forEach(([field, message]) => {
          setError(field, { type: 'server', message });
        });
      }
      const message = err.response?.data?.error || err.message || 'Failed to save setup';
      setSaveError(message);
      toast.error(`Error saving setup: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = (formErrors) => {
    setSaveError('');
    toast.error(getFirstErrorMessage(formErrors));
  };

  const daysOpen = calculateDaysOpen(weeklyHolidays, customHolidays);
  const dailyWorkingTime = calculateDailyWorkingMinutes(
    watch('openingTime'),
    watch('breakStartTime'),
    watch('breakEndTime'),
    watch('closingTime')
  );
  const hoursOpen = dailyWorkingTime.workingMinutes / 60;
  const chairCountForCapacity = Math.max(1, toNumber(watch('numberOfDentalChairs'), 1) || 1);
  const includedChairCount = Number(chairPricing?.includedChairs ?? chairSummary?.includedChairs);
  const grantedChairCount = Math.max(0, Number(chairSummary?.additionalActive) || 0);
  const unlockedChairCount = Number.isFinite(includedChairCount) ? includedChairCount + grantedChairCount : 0;
  const chargeableChairCount = Number.isFinite(includedChairCount)
    ? Math.max(0, chairCountForCapacity - unlockedChairCount)
    : 0;
  const annualChargePaise = chargeableChairCount * (Number(chairPricing?.pricePaise) || 0) * 12;
  const availableMinutes = daysOpen * dailyWorkingTime.workingMinutes * chairCountForCapacity;

  const patientVisitsLastMonth = Math.max(0, toNumber(watch('patientVisitsPerMonth')));
  const averageAppointmentDuration = Math.max(1, toNumber(watch('averageAppointmentDuration'), 30) || 30);
  const bookedMinutesLastMonth = patientVisitsLastMonth * averageAppointmentDuration;
  const utilization = availableMinutes > 0 ? (bookedMinutesLastMonth / availableMinutes) * 100 : 0;

  const totalOverhead =
    Math.max(0, toNumber(watch('rent'))) +
    Math.max(0, toNumber(watch('electricityBill'))) +
    Math.max(0, toNumber(watch('waterBill'))) +
    Math.max(0, toNumber(watch('miscellaneous'))) +
    Math.max(0, toNumber(watch('staffSalary'))) +
    Math.max(0, toNumber(watch('doctorSalary'))) +
    Math.max(0, toNumber(watch('assistantSalary'))) +
    Math.max(0, toNumber(watch('yourOwnSalary'))) +
    Math.max(0, toNumber(watch('softwares'))) +
    Math.max(0, toNumber(watch('onlineMarketing'))) +
    Math.max(0, toNumber(watch('offlineMarketing')));

  const effectiveMinutes = availableMinutes > 0 ? availableMinutes * (utilization / 100) : 0;
  const breakEvenPerMinute = effectiveMinutes > 0 ? totalOverhead / effectiveMinutes : 0;
  const equipmentValue = Math.max(0, toNumber(watch('equipmentValue')));
  const equipmentLifeYears = Math.max(1, toNumber(watch('equipmentLifeYears'), 5) || 5);
  const monthlyDepreciation =
    equipmentLifeYears > 0 ? equipmentValue / (equipmentLifeYears * 12) : 0;
  const validationMessages = Array.from(
    new Set(
      Object.values(errors)
        .map((error) => error?.message)
        .filter(Boolean)
    )
  );
  const watchedValues = watch();
  const clinicFields = ['clinicLegalName', 'leadDoctor', 'licenseNumber', 'mobileWhatsapp', 'upiId'];
  const operationsFields = ['openingTime', 'closingTime', 'numberOfDentalChairs', 'patientVisitsPerMonth', 'averageAppointmentDuration'];
  const costFields = [
    'rent',
    'electricityBill',
    'waterBill',
    'miscellaneous',
    'staffSalary',
    'doctorSalary',
    'assistantSalary',
    'yourOwnSalary',
    'softwares',
    'onlineMarketing',
    'offlineMarketing',
    'equipmentValue',
    'equipmentLifeYears',
    'setupInvestment',
  ];
  const financeFields = ['gstRate', 'incomeTaxRate'];
  const progressFields = [...clinicFields, ...operationsFields, ...costFields, ...financeFields];
  const completedFieldCount = progressFields.filter((field) => hasTrimmedValue(watchedValues[field])).length;
  const setupCompletion = Math.round((completedFieldCount / progressFields.length) * 100);
  const clinicComplete = clinicFields.every((field) => hasTrimmedValue(watchedValues[field]));
  const operationsComplete = operationsFields.every((field) => hasTrimmedValue(watchedValues[field])) && dailyWorkingTime.workingMinutes > 0;
  const costsComplete = [...costFields, ...financeFields].every((field) => hasTrimmedValue(watchedValues[field]));
  const financeComplete = financeFields.every((field) => hasTrimmedValue(watchedValues[field]));
  const setupSteps = [
    {
      key: 'clinic',
      title: 'Clinic Details',
      description: 'Legal, doctor, contact, logo',
      complete: clinicComplete,
      active: !clinicComplete,
    },
    {
      key: 'operations',
      title: 'Operations & Capacity',
      description: 'Hours, chairs, visits, minutes',
      complete: operationsComplete,
      active: clinicComplete && !operationsComplete,
    },
    {
      key: 'costs',
      title: 'Costs, Investment & Tax',
      description: 'Costs, depreciation, tax',
      complete: costsComplete,
      active: clinicComplete && operationsComplete && !costsComplete,
    },
  ];

  const scrollToSetupSection = (key) => {
    const target = sectionRefs.current[key];
    if (!target) return;
    setActiveSetupStep(key);
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setupJourneySteps = [
    {
      title: 'Clinic Setup',
      active: true,
    },
    {
      title: 'Inventory',
      action: () => navigate('/inventory'),
    },
    {
      title: 'Pricing',
      action: () => navigate('/pricing'),
    },
  ];

  const renderSetupJourneyThread = () => (
    <div className="w-full rounded-3xl border border-slate-200 bg-slate-50/80 px-4 py-4 lg:max-w-sm">
      <div className="relative mx-auto max-w-sm px-4 pb-1 pt-2">
        <div className="absolute left-9 right-9 top-6 h-0.5 rounded-full bg-slate-200" />
        <div className="absolute left-9 top-6 h-0.5 w-0 rounded-full bg-blue-600" />
        <div className="relative grid grid-cols-3 gap-2">
        {setupJourneySteps.map((step, index) => {
          const StepShell = step.action ? 'button' : 'div';
          return (
            <StepShell
              key={step.title}
              type={step.action ? 'button' : undefined}
              onClick={step.action}
              className="group flex min-w-0 flex-col items-center text-center"
            >
              <span
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition ${
                  step.active
                    ? 'border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.18)]'
                    : 'border-slate-200 bg-white text-slate-500 group-hover:border-blue-200 group-hover:text-blue-700'
                }`}
              >
                {index + 1}
              </span>
              <span
                className={`mt-2 block max-w-[92px] text-xs font-semibold leading-4 ${
                  step.active ? 'text-blue-700' : 'text-slate-500 group-hover:text-blue-700'
                }`}
              >
                {step.title}
              </span>
            </StepShell>
          );
        })}
        </div>
      </div>
    </div>
  );

  const renderSetupPage = (financeStatus) => initializing ? (
    <div className="min-h-full bg-[#F7F8FA] p-4 md:p-10">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-700">Setup Dektal 360</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">Preparing your clinic workspace</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Loading saved clinic setup, Finance Lock state, operating capacity, and cost values.
          </p>
        </div>
      </div>
    </div>
  ) : (
    <div className="relative min-h-full overflow-hidden bg-[#F7F9FC] p-4 md:p-10">
      <div className="pointer-events-none absolute left-[8%] top-[-8rem] h-80 w-80 rounded-full bg-blue-100/35 blur-3xl" />
      <div className="pointer-events-none absolute right-[7%] top-32 h-72 w-72 rounded-full bg-cyan-100/20 blur-3xl" />
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate className="mx-auto max-w-7xl">
        <div className="animate-setup-fade-up relative mb-6 overflow-hidden rounded-[28px] border border-slate-200/90 bg-white/95 shadow-[0_18px_54px_rgba(15,23,42,0.07)] backdrop-blur-xl">
          <div className="relative p-5 md:p-6">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
            <div className="pointer-events-none absolute right-8 top-6 h-28 w-28 rounded-full bg-blue-50 blur-2xl" />
            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                  <Stethoscope className="h-4 w-4" />
                  Clinic foundation
                </div>
                <h1 className="mt-4 bg-gradient-to-r from-blue-800 via-blue-600 to-slate-800 bg-clip-text text-4xl font-semibold tracking-tight text-transparent drop-shadow-[0_10px_22px_rgba(37,99,235,0.12)] md:text-[44px]">
                  Setup Your Clinic
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
                  Just three quick steps and your clinic is ready to run.
                </p>
              </div>

              {renderSetupJourneyThread()}
            </div>
          </div>
        </div>

        {loadError ? (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Failed to load saved setup values: {loadError}
          </div>
        ) : null}
        {monthlyPerformance?.promptAvailable ? (
          <section className="mb-6 rounded-[24px] border border-blue-200 bg-blue-50/80 p-5 shadow-sm" aria-labelledby="monthly-performance-title">
            <h2 id="monthly-performance-title" className="text-lg font-semibold text-slate-900">{monthlyPerformance.message}</h2>
            <div className="mt-3 space-y-1 text-sm text-slate-700">
              <p><strong>{monthlyPerformance.qualifyingVisitCount}</strong> paid patient visits</p>
              <p>Average appointment duration: <strong>{monthlyPerformance.averageAppointmentDuration ? `${monthlyPerformance.averageAppointmentDuration} minutes` : 'No valid measured duration available'}</strong></p>
              <p className="text-xs text-slate-500">Measured duration available for {monthlyPerformance.validAppointmentDurationCount} visit(s). Scheduled appointment time is not used.</p>
              {(monthlyPerformance.treatments || []).slice(0, 3).map((row) => (
                <p key={row.key}>{row.treatmentName}: {row.count} case(s){row.averageDurationMinutes ? ` · ${row.averageDurationMinutes} min average` : ''}</p>
              ))}
              {monthlyPerformance.ambiguousTreatmentDurationExclusions > 0 ? <p className="text-xs text-amber-700">{monthlyPerformance.ambiguousTreatmentDurationExclusions} multi-treatment occurrence(s) were excluded from duration averages because time could not be assigned reliably.</p> : null}
            </div>
            <p className="mt-3 text-sm text-slate-700">Would you like to update your Capacity Assumptions and treatment durations using these actual results?</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button type="button" onClick={() => setMonthlyReviewOpen(true)} className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">Review &amp; Update</button>
              <button type="button" onClick={keepMonthlyValues} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">Keep Current Values</button>
            </div>
          </section>
        ) : null}
        {monthlyReviewOpen && monthlyPerformance ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setMonthlyReviewOpen(false); }}>
            <section role="dialog" aria-modal="true" aria-labelledby="monthly-review-title" className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl md:p-7">
              <div className="flex items-start justify-between gap-4"><div><h2 id="monthly-review-title" className="text-2xl font-semibold text-slate-900">Review {monthlyPerformance.period?.label} actuals</h2><p className="mt-1 text-sm text-slate-500">Only checked rows will change. Selling Prices, lab costs, consultant fees, and material rates stay unchanged.</p></div><button type="button" aria-label="Close review" onClick={() => setMonthlyReviewOpen(false)} className="h-10 w-10 rounded-full border text-xl hover:bg-slate-50">×</button></div>
              <div className="mt-5 overflow-x-auto"><table className="w-full min-w-[680px] text-left text-sm"><thead><tr className="border-b text-slate-500"><th className="py-3">Value</th><th className="py-3 text-right">Current value</th><th className="py-3 text-right">Last month's actual value</th><th className="py-3 text-center">Update</th></tr></thead><tbody>
                <tr className="border-b"><td className="py-3 font-medium">Patient Visits Last Month</td><td className="py-3 text-right">{monthlyPerformance.current?.patientVisitsPerMonth ?? '-'}</td><td className="py-3 text-right">{monthlyPerformance.qualifyingVisitCount}</td><td className="py-3 text-center"><input aria-label="Update patient visits" type="checkbox" checked={monthlySelections.patientVisits} onChange={(event) => setMonthlySelections((value) => ({ ...value, patientVisits: event.target.checked }))} /></td></tr>
                {monthlyPerformance.averageAppointmentDuration ? <tr className="border-b"><td className="py-3 font-medium">Average Appointment Duration</td><td className="py-3 text-right">{monthlyPerformance.current?.averageAppointmentDuration ?? '-'} min</td><td className="py-3 text-right">{monthlyPerformance.averageAppointmentDuration} min</td><td className="py-3 text-center"><input aria-label="Update average appointment duration" type="checkbox" checked={monthlySelections.averageAppointmentDuration} onChange={(event) => setMonthlySelections((value) => ({ ...value, averageAppointmentDuration: event.target.checked }))} /></td></tr> : null}
                {(monthlyPerformance.treatments || []).filter((row) => row.eligibleForUpdate).map((row) => <tr className="border-b" key={row.key}><td className="py-3 font-medium">{row.treatmentName} Duration <span className="block text-xs font-normal text-slate-500">{row.validDurationCount} measured case(s)</span></td><td className="py-3 text-right">{row.currentDurationMinutes} min</td><td className="py-3 text-right">{row.averageDurationMinutes} min</td><td className="py-3 text-center"><input aria-label={`Update ${row.treatmentName} duration`} type="checkbox" checked={monthlySelections.treatmentIds.includes(row.treatmentId)} onChange={(event) => setMonthlySelections((value) => ({ ...value, treatmentIds: event.target.checked ? [...new Set([...value.treatmentIds, row.treatmentId])] : value.treatmentIds.filter((id) => id !== row.treatmentId) }))} /></td></tr>)}
              </tbody></table></div>
              <div className="mt-6 flex flex-wrap justify-end gap-3"><button type="button" onClick={() => setMonthlyReviewOpen(false)} className="rounded-xl border px-4 py-2.5 font-semibold">Cancel</button><button type="button" onClick={() => setMonthlySelections({ patientVisits: true, averageAppointmentDuration: Boolean(monthlyPerformance.averageAppointmentDuration), treatmentIds: (monthlyPerformance.treatments || []).filter((row) => row.eligibleForUpdate).map((row) => row.treatmentId) })} className="rounded-xl border border-blue-200 px-4 py-2.5 font-semibold text-blue-700">Accept all eligible</button><button type="button" disabled={monthlyPerformanceLoading} onClick={applyMonthlyValues} className="rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white disabled:opacity-50">{monthlyPerformanceLoading ? 'Applying…' : 'Apply selected values'}</button></div>
            </section>
          </div>
        ) : null}
        {submitCount > 0 && validationMessages.length ? (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="font-semibold">Please fix the highlighted setup fields before launching Dektal 360.</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {validationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-8">
            <section
              ref={(node) => {
                sectionRefs.current.clinic = node;
              }}
              data-setup-step="clinic"
              className="scroll-mt-8 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_42px_rgba(15,23,42,0.05)] transition-all duration-300 hover:shadow-[0_22px_55px_rgba(15,23,42,0.07)] md:p-8"
            >
              <SectionHeader
                icon={Building2}
                title="Clinic Details"
                description="This becomes your prescription and invoice identity. It stays inside your tenant workspace."
              />

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <FieldShell label="Clinic Legal Name *" error={errors.clinicLegalName}>
                  <input
                    {...register('clinicLegalName', {
                      required: 'Clinic Legal Name is required.',
                      validate: (value) => hasTrimmedValue(value) || 'Clinic Legal Name is required.',
                    })}
                    className={getInputClass(Boolean(errors.clinicLegalName))}
                    placeholder="Yash Dental Clinic"
                  />
                </FieldShell>

                <FieldShell label="Lead Doctor Name *" error={errors.leadDoctor}>
                  <input
                    {...register('leadDoctor', {
                      required: 'Lead Doctor Name is required.',
                      validate: (value) => hasTrimmedValue(value) || 'Lead Doctor Name is required.',
                    })}
                    className={getInputClass(Boolean(errors.leadDoctor))}
                    placeholder="Dr. Yash"
                  />
                </FieldShell>

                <FieldShell label="License Number *" error={errors.licenseNumber} helper="Required. Free-text, trimmed on save, maximum 100 characters.">
                  <input
                    {...register('licenseNumber', {
                      required: 'License Number is required.',
                      validate: (value) => {
                        const trimmedValue = String(value || '').trim();
                        if (!trimmedValue) return 'License Number is required.';
                        return trimmedValue.length <= 100 || 'License Number must be 100 characters or fewer.';
                      },
                    })}
                    maxLength={100}
                    className={getInputClass(Boolean(errors.licenseNumber))}
                    placeholder="Enter clinic license number"
                  />
                </FieldShell>

                <FieldShell label="Mobile (WhatsApp) *" error={errors.mobileWhatsapp}>
                  <input
                    {...register('mobileWhatsapp', {
                      required: 'Mobile (WhatsApp) is required.',
                      validate: (value) => {
                        if (!hasTrimmedValue(value)) return 'Mobile (WhatsApp) is required.';
                        return (
                          PHONE_REGEX.test(String(value || '').replace(/\D/g, '').slice(-10)) ||
                          'Invalid WhatsApp number. Must be 10 digits starting with 6/7/8/9.'
                        );
                      },
                    })}
                    className={getInputClass(Boolean(errors.mobileWhatsapp))}
                    placeholder="10-digit WhatsApp number"
                  />
                </FieldShell>

                <FieldShell label="Clinic UPI ID (Optional)" error={errors.upiId} helper="Add this only if you want Dektal to include a direct UPI payment link in patient payment messages.">
                  <input
                    {...register('upiId', {
                      validate: (value) => {
                        if (!hasTrimmedValue(value)) return true;
                        return UPI_REGEX.test(String(value || '').trim()) || 'Please enter a valid UPI ID (name@bank)';
                      },
                    })}
                    className={getInputClass(Boolean(errors.upiId))}
                    placeholder="e.g. clinicname@oksbi"
                  />
                </FieldShell>

                <SliderNumberField
                  label="Number of Dental Chairs *"
                  field="numberOfDentalChairs"
                  value={watchedValues.numberOfDentalChairs}
                  register={register}
                  setValue={setValue}
                  error={errors.numberOfDentalChairs}
                  min={1}
                  max={20}
                  placeholder="1 to 20 chairs"
                  helper="Chair count drives total available clinical minutes."
                  rules={{
                    required: 'Number of dental chairs is required.',
                    validate: (value) => {
                      const parsed = Math.trunc(toNumber(value, NaN));
                      if (!Number.isFinite(parsed)) return 'Number of dental chairs is required.';
                      return (parsed >= 1 && parsed <= 20) || 'Number of dental chairs must be between 1 and 20.';
                    },
                  }}
                />
                {Number.isFinite(includedChairCount) && chairCountForCapacity > includedChairCount ? (
                  <div className="md:col-span-2 rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 to-teal-50 p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-[#0c1f3d]">Additional-chair options</h3>
                        <p className="mt-1 text-sm text-slate-600">Selecting more chairs does not unlock them automatically. Please pay or use a Referral Credit first, then save your updated chair count.</p>
                      </div>
                      {chairPricingLoading ? <span className="text-sm text-slate-500">Loading pricing…</span> : null}
                    </div>
                    {chairPricingError ? (
                      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Pricing unavailable. Please contact Dektal support. No estimate has been applied.
                      </div>
                    ) : (
                      <>
                        <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                          <div className="rounded-2xl bg-white p-4"><dt className="text-xs font-medium text-slate-500">Included chairs</dt><dd className="mt-1 text-xl font-semibold text-slate-900">{includedChairCount}</dd></div>
                          <div className="rounded-2xl bg-white p-4"><dt className="text-xs font-medium text-slate-500">Active granted chairs</dt><dd className="mt-1 text-xl font-semibold text-slate-900">{grantedChairCount}</dd></div>
                          <div className="rounded-2xl bg-white p-4"><dt className="text-xs font-medium text-slate-500">Chairs needing unlock</dt><dd className="mt-1 text-xl font-semibold text-blue-700">{chargeableChairCount}</dd></div>
                        </dl>
                        <div className="mt-5" role="tablist" aria-label="Additional chair unlock options">
                          <div className="flex rounded-2xl bg-white p-1">
                            <button type="button" role="tab" aria-selected={chairUnlockTab === 'payment'} onClick={() => setChairUnlockTab('payment')} className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold ${chairUnlockTab === 'payment' ? 'bg-blue-700 text-white' : 'text-slate-600'}`}>Pay &amp; Unlock</button>
                            <button type="button" role="tab" aria-selected={chairUnlockTab === 'referral'} onClick={() => setChairUnlockTab('referral')} className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold ${chairUnlockTab === 'referral' ? 'bg-teal-600 text-white' : 'text-slate-600'}`}>Invite &amp; Unlock</button>
                          </div>
                          {chairUnlockTab === 'payment' ? (
                            <div role="tabpanel" className="mt-4 rounded-2xl bg-white p-5">
                              <div className="flex items-start gap-3"><CreditCard className="mt-0.5 h-5 w-5 text-blue-700" /><div><p className="font-semibold text-slate-900">Pay to activate {chargeableChairCount} additional chair{chargeableChairCount === 1 ? '' : 's'}.</p><p className="mt-1 text-sm text-slate-600">Estimated annual amount: <strong>{formatInrFromPaise(annualChargePaise)}/year</strong> ({formatInrFromPaise(chairPricing.pricePaise)} per chair/month × 12). {chairPricing.paymentEnabled ? `Razorpay ${chairPricing.razorpayMode === 'live' ? 'Live' : 'Test'} mode is available.` : 'Razorpay payment configuration is unavailable.'}</p></div></div>
                              <button type="button" onClick={() => startChairPayment({ requestedChairCount: chairCountForCapacity })} disabled={chairPaymentProcessing || !chairPricing.paymentEnabled} title={!chairPricing.paymentEnabled ? 'Razorpay payment configuration is unavailable.' : ''} className="mt-4 rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300">{chairPricing.paymentEnabled ? 'Make Payment' : 'Payment Unavailable'}</button>
                              {chairPaymentError ? <p role="alert" className="mt-3 text-sm font-semibold text-amber-700">{chairPaymentError}</p> : null}
                              {chairPaymentReceipt ? <p className="mt-3 text-sm font-semibold text-emerald-700">Payment verified. Effective chair limit refreshed to {chairPaymentReceipt.chairSummary?.effectiveLimit}.</p> : null}
                            </div>
                          ) : (
                            <div role="tabpanel" className="mt-4 space-y-3">
                              <div className="rounded-2xl bg-white p-4"><p className="font-semibold text-slate-900">Free Additional Chair</p><p className="mt-1 text-sm text-slate-600">Invite 1 doctor who buys an annual subscription → unlock 1 additional chair free for one annual period. Requires an active annual subscription and does not auto-renew.</p></div>
                              <button type="button" onClick={() => navigate('/addons#share-your-referral')} className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white"><UserPlus className="h-4 w-4" />Invite a Friend</button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}

                <div className="md:col-span-2">
                  <FieldShell label="Clinic Address">
                    <input {...register('clinicAddress')} className="input-modern" placeholder="Clinic Address" />
                  </FieldShell>
                </div>

                <div className="md:col-span-2">
                  <FieldShell label="GST Number" error={errors.gstNumber}>
                    <input
                      {...register('gstNumber', {
                        validate: (value) => {
                          if (!hasTrimmedValue(value)) return true;
                          return GST_REGEX.test(String(value || '').trim().toUpperCase()) || 'GST number must be 15 characters.';
                        },
                      })}
                      className={getInputClass(Boolean(errors.gstNumber))}
                      placeholder="15-character GST number"
                    />
                  </FieldShell>
                </div>

                <input type="hidden" {...register('clinicLogo')} />

                <div className="md:col-span-2">
                  <label className="label-modern">Clinic Logo / Doctor Photo</label>
                  <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-5">
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="block w-full text-sm text-slate-600" />
                    {logoPreview ? (
                      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-start">
                        <img src={logoPreview} alt="Clinic Logo Preview" className="h-24 w-24 rounded-2xl border border-slate-200 bg-white object-cover" />
                        <button type="button" onClick={removeLogo} className="btn-secondary">
                          Remove Logo
                        </button>
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-slate-500">
                        Upload clinic logo or doctor photo for prescription and invoice letterhead.
                      </p>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="label-modern">Dektal InkPad</label>
                  <div className="rounded-[24px] border border-indigo-200 bg-indigo-50/50 p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Prescription Background</h3>
                        <p className="mt-1 text-xs leading-5 text-slate-600">Upload a complete JPG or PNG prescription letterhead. It is stored separately from the clinic logo and can be selected per InkPad page.</p>
                        <label className="mt-4 inline-flex cursor-pointer items-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">
                          {inkpadBackgroundBusy ? 'Uploading…' : inkpadBackground.exists ? 'Replace Background' : 'Upload Background'}
                          <input type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" disabled={inkpadBackgroundBusy} onChange={uploadInkpadBackground} className="sr-only" />
                        </label>
                        {inkpadBackground.exists ? <button type="button" disabled={inkpadBackgroundBusy} onClick={removeInkpadBackground} className="btn-secondary ml-2">Remove</button> : null}
                        <p className="mt-2 text-[11px] text-slate-500">JPG or PNG · maximum 8 MB</p>
                      </div>
                      {inkpadBackgroundPreview ? <img src={inkpadBackgroundPreview} alt="Dektal InkPad prescription background preview" className="max-h-72 w-full max-w-[220px] rounded-xl border border-slate-200 bg-white object-contain shadow-sm" /> : <div className="flex aspect-[1/1.414] w-full max-w-[180px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-4 text-center text-xs text-slate-500">No InkPad prescription background uploaded</div>}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              ref={(node) => {
                sectionRefs.current.operations = node;
              }}
              data-setup-step="operations"
              className="scroll-mt-8 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_42px_rgba(15,23,42,0.05)] transition-all duration-300 hover:shadow-[0_22px_55px_rgba(15,23,42,0.07)] md:p-8"
            >
              <SectionHeader
                icon={Activity}
                title="Operations & Capacity"
                description="These timings turn clinic availability into pricing and utilization intelligence."
              />

              <div className="space-y-7">
                <div className="rounded-[24px] border border-blue-100/70 bg-[#F8FAFF] p-5 shadow-[0_10px_28px_rgba(15,23,42,0.025)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Working Hours</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Enter clinic working hours. Add break time if you close in the afternoon.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <FieldShell label="Opening Time *" error={errors.openingTime}>
                      <input
                        {...register('openingTime', {
                          required: 'Opening Time is required.',
                          validate: (value) => hasTrimmedValue(value) || 'Opening Time is required.',
                        })}
                        type="time"
                        className={getInputClass(Boolean(errors.openingTime))}
                      />
                    </FieldShell>

                    <FieldShell label="Break Start Time" error={errors.breakStartTime}>
                      <input
                        {...register('breakStartTime')}
                        type="time"
                        className={getInputClass(Boolean(errors.breakStartTime))}
                      />
                    </FieldShell>

                    <FieldShell label="Break End Time" error={errors.breakEndTime}>
                      <input
                        {...register('breakEndTime')}
                        type="time"
                        className={getInputClass(Boolean(errors.breakEndTime))}
                      />
                    </FieldShell>

                    <FieldShell label="Closing Time *" error={errors.closingTime}>
                      <input
                        {...register('closingTime', {
                          required: 'Closing Time is required.',
                          validate: (value) => {
                            if (!hasTrimmedValue(value)) return 'Closing Time is required.';
                            const workingTime = calculateDailyWorkingMinutes(
                              watch('openingTime'),
                              watch('breakStartTime'),
                              watch('breakEndTime'),
                              value
                            );
                            return workingTime.workingMinutes > 0 || 'Net working time must be greater than zero.';
                          },
                        })}
                        type="time"
                        className={getInputClass(Boolean(errors.closingTime))}
                      />
                    </FieldShell>

                    <div className="md:col-span-2">
                      <div className="rounded-2xl border border-blue-100 bg-white/80 p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Net Working Time / Day</p>
                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              Closing minus opening, after break time.
                            </p>
                          </div>
                          <p className="text-lg font-semibold text-blue-700">
                            {dailyWorkingTime.workingMinutes > 0 ? formatMinutesAsHours(dailyWorkingTime.workingMinutes) : 'Setup incomplete'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-[24px] border border-blue-100/80 bg-[#F8FBFF] p-5 shadow-[0_10px_28px_rgba(37,99,235,0.035)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Weekly Schedule</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Pick the days your clinic stays closed.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const next = weeklyHolidays.includes(day)
                            ? weeklyHolidays.filter((item) => item !== day)
                            : [...weeklyHolidays, day];
                          setWeeklyHolidays(next);
                        }}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                          weeklyHolidays.includes(day)
                            ? 'border-rose-200 bg-rose-50 text-rose-700 shadow-[0_8px_18px_rgba(225,29,72,0.08)]'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-rose-200 hover:text-rose-700'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-[24px] border border-blue-100/70 bg-[#F9FCFF] p-5 shadow-[0_10px_28px_rgba(15,23,42,0.025)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Custom Holidays</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Add extra closure dates.</p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input type="date" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} className="input-modern flex-1" />
                    <button
                      type="button"
                      onClick={() => {
                        if (newHoliday && !customHolidays.includes(newHoliday)) {
                          setCustomHolidays([...customHolidays, newHoliday]);
                        }
                        setNewHoliday('');
                      }}
                      className="btn-secondary whitespace-nowrap"
                    >
                      Add Date
                    </button>
                  </div>

                  {customHolidays.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {customHolidays.map((date) => (
                        <span key={date} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
                          {new Date(date).toLocaleDateString()}
                          <button
                            type="button"
                            onClick={() => setCustomHolidays(customHolidays.filter((item) => item !== date))}
                            className="text-lg font-bold leading-none text-red-500 hover:text-red-700"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-[24px] border border-blue-100/70 bg-[#F8FBFF] p-5 shadow-[0_10px_28px_rgba(37,99,235,0.035)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Capacity Assumptions</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Initial inputs until real appointment data builds up.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <SliderNumberField
                      label="Patient Visits Last Month"
                      field="patientVisitsPerMonth"
                      value={watchedValues.patientVisitsPerMonth}
                      register={register}
                      setValue={setValue}
                      min={0}
                      max={3000}
                      step={1}
                      placeholder="Patient Visits Last Month"
                      helper="Helps estimate booked clinical time."
                    />

                    <SliderNumberField
                      label="Average Appointment Duration (min)"
                      field="averageAppointmentDuration"
                      value={watchedValues.averageAppointmentDuration}
                      register={register}
                      setValue={setValue}
                      error={errors.averageAppointmentDuration}
                      min={1}
                      max={480}
                      step={1}
                      placeholder="Default 30 minutes"
                      helper="Use your usual appointment length."
                      rules={{
                        validate: (value) => {
                          const parsed = Math.trunc(toNumber(value, NaN));
                          if (!Number.isFinite(parsed)) return 'Average appointment duration must be between 1 minute and 8 hours.';
                          return (parsed >= 1 && parsed <= 480) || 'Average appointment duration must be between 1 minute and 8 hours.';
                        },
                      }}
                    />

                    <SliderNumberField
                      label="Buffer Between Appointments (min)"
                      field="bufferMinutes"
                      value={watchedValues.bufferMinutes}
                      register={register}
                      setValue={setValue}
                      error={errors.bufferMinutes}
                      min={0}
                      max={240}
                      step={1}
                      placeholder="Default 5 minutes"
                      helper="Helps plan chair turnover."
                      rules={{
                        validate: (value) => {
                          const parsed = Math.trunc(toNumber(value, NaN));
                          if (!Number.isFinite(parsed)) return true;
                          return (parsed >= 0 && parsed <= 240) || 'Buffer between appointments must be between 0 and 240 minutes.';
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="rounded-[24px] border border-blue-100/70 bg-white p-5 shadow-[0_10px_28px_rgba(15,23,42,0.025)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Capacity Summary / Auto Calculated Metrics</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Dektal converts your timings into clinic capacity.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <ReadOnlyRange label="Monthly Working Days" value={daysOpen} min={0} max={31} helper="Weekly offs and custom holidays included." />
                    <ReadOnlyRange label="Net Hours / Day" value={hoursOpen} min={0} max={24} suffix=" hrs" helper="Break time is subtracted." />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="metric-card">
                    <div className="metric-label">Available Chair Minutes</div>
                    <div className="metric-value">{Math.round(availableMinutes)}</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Booked Minutes Last Month</div>
                    <div className="metric-value">{Math.round(bookedMinutesLastMonth)}</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Clinic Utilization</div>
                    <div className="metric-value text-[22px]">{utilization.toFixed(2)}%</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Chairs</div>
                    <div className="metric-value">{toNumber(watchedValues.numberOfDentalChairs, 1)}</div>
                  </div>
                </div>
              </div>
            </section>

            <section
              ref={(node) => {
                sectionRefs.current.costs = node;
              }}
              data-setup-step="costs"
              className="scroll-mt-8 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_42px_rgba(15,23,42,0.05)] transition-all duration-300 hover:shadow-[0_22px_55px_rgba(15,23,42,0.07)] md:p-8"
            >
              <SectionHeader
                icon={Calculator}
                title="Costs, Investment & Tax"
                description="Separate operating costs, clinic investment, depreciation, tax, and loan interest."
              />

              <div className="space-y-7">
                <div className="rounded-[24px] border border-blue-100/70 bg-[#F9FCFF] p-5 shadow-[0_10px_28px_rgba(15,23,42,0.025)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Monthly Operating Costs</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Regular monthly costs used for break-even guidance.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <FieldShell label="Rent"><input {...register('rent')} type="number" min="0" placeholder="Rent" className="input-modern" /></FieldShell>
                    <FieldShell label="Electricity"><input {...register('electricityBill')} type="number" min="0" placeholder="Electricity" className="input-modern" /></FieldShell>
                    <FieldShell label="Water"><input {...register('waterBill')} type="number" min="0" placeholder="Water" className="input-modern" /></FieldShell>
                    <FieldShell label="Miscellaneous"><input {...register('miscellaneous')} type="number" min="0" placeholder="Miscellaneous" className="input-modern" /></FieldShell>
                    <FieldShell label="Staff Salary"><input {...register('staffSalary')} type="number" min="0" placeholder="Staff Salary" className="input-modern" /></FieldShell>
                    <FieldShell label="Doctor Salary"><input {...register('doctorSalary')} type="number" min="0" placeholder="Doctor Salary" className="input-modern" /></FieldShell>
                    <FieldShell label="Assistant Salary"><input {...register('assistantSalary')} type="number" min="0" placeholder="Assistant Salary" className="input-modern" /></FieldShell>
                    <FieldShell label="Your Own Salary"><input {...register('yourOwnSalary')} type="number" min="0" placeholder="Your Own Salary" className="input-modern" /></FieldShell>
                    <FieldShell label="Softwares"><input {...register('softwares')} type="number" min="0" placeholder="Softwares" className="input-modern" /></FieldShell>
                    <FieldShell label="Online Marketing"><input {...register('onlineMarketing')} type="number" min="0" placeholder="Online Marketing" className="input-modern" /></FieldShell>
                    <FieldShell label="Offline Marketing"><input {...register('offlineMarketing')} type="number" min="0" placeholder="Offline Marketing" className="input-modern" /></FieldShell>
                  </div>
                </div>

                <div className="rounded-[24px] border border-blue-100/70 bg-[#F8FBFF] p-5 shadow-[0_10px_28px_rgba(37,99,235,0.035)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Investment & Depreciation</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Keep one-time investment separate from monthly expenses.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <FieldShell
                      label="Total Clinic Investment (Rs)"
                      helper="One-time money invested in clinic build, interiors, equipment, furniture, instruments, or launch capital. Used for ROI and recovery tracking only."
                    >
                      <input {...register('setupInvestment')} type="number" min="0" className="input-modern" placeholder="Total Clinic Investment" />
                    </FieldShell>
                    <FieldShell
                      label="Depreciable Equipment Value (Rs)"
                      helper="Only equipment that loses value over time, such as chair, RVG, compressor, autoclave, or scanner."
                    >
                      <input {...register('equipmentValue')} type="number" min="0" placeholder="Depreciable Equipment Value" className="input-modern" />
                    </FieldShell>
                    <div className="md:col-span-2">
                      <SliderNumberField
                        label="Equipment Life in Years"
                        field="equipmentLifeYears"
                        value={watchedValues.equipmentLifeYears}
                        register={register}
                        setValue={setValue}
                        min={1}
                        max={30}
                        step={1}
                        placeholder="Equipment Life (Years)"
                        helper="Used to calculate monthly depreciation."
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-[24px] border border-blue-100/70 bg-[#F8FAFF] p-5 shadow-[0_10px_28px_rgba(15,23,42,0.025)]">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">Tax & Loan Settings</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">For business tracking. Confirm filing with your CA/accountant.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                    <FieldShell label="GST on Invoice (%) - Optional" error={errors.gstRate} helper="Keep 0 if GST is not applicable. Used only for invoice tax calculation.">
                      <input
                        {...register('gstRate', {
                          validate: (value) => {
                            const parsed = toNumber(value, NaN);
                            if (!Number.isFinite(parsed)) return 'GST rate must be between 0 and 100.';
                            return (parsed >= 0 && parsed <= 100) || 'GST rate must be between 0 and 100.';
                          },
                        })}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className={getInputClass(Boolean(errors.gstRate))}
                        placeholder="Default 0"
                      />
                    </FieldShell>

                    <FieldShell label="Estimated Income Tax Rate on Profit (%)" error={errors.incomeTaxRate} helper="Used only for estimated profit reports. Ask your CA/accountant if unsure.">
                      <input
                        {...register('incomeTaxRate', {
                          validate: (value) => {
                            const parsed = toNumber(value, NaN);
                            if (!Number.isFinite(parsed)) return 'Income tax rate must be between 0 and 100.';
                            return (parsed >= 0 && parsed <= 100) || 'Income tax rate must be between 0 and 100.';
                          },
                        })}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className={getInputClass(Boolean(errors.incomeTaxRate))}
                        placeholder="Default 25"
                      />
                    </FieldShell>

                    <FieldShell label="Monthly Loan Interest (Rs)" helper="Enter only monthly interest on clinic or equipment loan. Keep 0 if no loan.">
                      <input {...register('interest')} type="number" min="0" step="0.01" className="input-modern" placeholder="Monthly Loan Interest" />
                    </FieldShell>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="metric-card">
                    <div className="metric-label">Total Monthly Overhead</div>
                    <div className="metric-value">{formatCurrency(totalOverhead)}</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label text-blue-700">Break-Even Cost / Min</div>
                    <div className="metric-value font-semibold text-blue-700">
                      {breakEvenPerMinute > 0 ? `Rs ${breakEvenPerMinute.toFixed(2)}` : 'Setup incomplete'}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label text-slate-500">Monthly Depreciation</div>
                    <div className="metric-value text-slate-700">Rs {monthlyDepreciation.toFixed(2)}</div>
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm leading-6 text-amber-900">
                  These values support business profitability reports and treatment pricing guidance. Tax values are estimates for business tracking. Please confirm statutory tax filing with your CA/accountant.
                </div>
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_42px_rgba(15,23,42,0.05)] md:p-8">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                    <Rocket className="h-4 w-4" />
                    Launch Workspace
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">Ready when your clinic values are complete.</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                    Save safely for this clinic only. Master tables stay untouched.
                  </p>
                </div>
                <button type="submit" disabled={loading} className="tap-target w-full rounded-2xl bg-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(29,78,216,0.18)] transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto">
                  {loading ? 'Saving...' : 'Save & Launch Dektal 360'}
                </button>
              </div>
            </section>

            {saveError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                Save failed: {saveError}
              </div>
            ) : null}
          </div>

          <aside className="xl:sticky xl:top-6 xl:self-start">
            <div className="space-y-5">
              <div className="overflow-hidden rounded-[28px] border border-blue-100/80 bg-white/95 shadow-[0_18px_48px_rgba(15,23,42,0.08)] backdrop-blur">
                <div className="relative overflow-hidden border-b border-blue-100/80 bg-[linear-gradient(135deg,rgba(37,99,235,0.16),rgba(226,232,240,0.5)_48%,rgba(219,234,254,0.42))] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">
                  <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-blue-300/25 blur-2xl" />
                  <div className="absolute -bottom-10 left-8 h-24 w-24 rounded-full bg-cyan-200/25 blur-2xl" />
                  <div className="relative">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-800">Live setup summary</p>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">{watchedValues.clinicLegalName || 'Clinic name pending'}</h2>
                    <p className="mt-1 text-sm text-slate-600">{watchedValues.leadDoctor || 'Lead doctor pending'}</p>
                  </div>
                </div>
                <div className="space-y-4 p-5">
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                    <span className="text-sm font-semibold text-slate-500">Setup completion</span>
                    <span className="text-lg font-semibold text-slate-900">{setupCompletion}%</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                    <span className="text-sm font-semibold text-slate-500">Dental chairs</span>
                    <span className="text-lg font-semibold text-slate-900">{toNumber(watchedValues.numberOfDentalChairs, 1)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                    <span className="text-sm font-semibold text-slate-500">Available minutes</span>
                    <span className="text-lg font-semibold text-slate-900">{Math.round(availableMinutes)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                    <span className="text-sm font-semibold text-slate-500">Monthly overhead</span>
                    <span className="text-lg font-semibold text-slate-900">{formatCurrency(totalOverhead)}</span>
                  </div>
                  <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                      <IndianRupee className="h-4 w-4" />
                      Finance Lock status
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      Unlocked for this session. Manage PIN and lock settings below.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-bold text-slate-900">Doctor-friendly note</p>
                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      Enter real costs. Dektal uses them for accurate pricing and reports.
                    </p>
                  </div>
                </div>
              </div>

              <FinanceLockSettingsPanel compact status={financeStatus} onChanged={financeStatus.refresh} />

              <div className="rounded-[28px] border border-blue-100/80 bg-white/95 p-5 shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
                <div className="mb-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-700">Setup sections</p>
                  <h3 className="mt-2 text-lg font-semibold tracking-tight text-slate-900">Follow the setup thread</h3>
                </div>
                <div className="space-y-0">
                  {setupSteps.map((step, index) => {
                    const isActive = activeSetupStep === step.key || step.active;
                    const isLast = index === setupSteps.length - 1;

                    return (
                      <button
                        key={step.key}
                        type="button"
                        onClick={() => scrollToSetupSection(step.key)}
                        className="group grid w-full grid-cols-[34px_minmax(0,1fr)] gap-3 text-left focus:outline-none"
                      >
                        <div className="flex flex-col items-center">
                          <span
                            className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold transition ${
                              step.complete
                                ? 'border-blue-200 bg-blue-50 text-blue-700'
                                : isActive
                                  ? 'border-blue-600 bg-blue-600 text-white shadow-[0_10px_20px_rgba(37,99,235,0.18)]'
                                  : 'border-slate-200 bg-white text-slate-400 group-hover:border-blue-200 group-hover:text-blue-600'
                            }`}
                          >
                            {step.complete ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                          </span>
                          {!isLast ? (
                            <span className={`h-10 w-px ${step.complete ? 'bg-blue-200' : 'bg-slate-200'}`} />
                          ) : null}
                        </div>
                        <div className={`rounded-2xl px-3 py-2 transition ${
                          isActive ? 'bg-blue-50/80' : 'group-hover:bg-slate-50'
                        }`}>
                          <p className={`text-sm font-semibold ${isActive ? 'text-blue-800' : 'text-slate-800'}`}>{step.title}</p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">{step.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_12px_30px_rgba(15,23,42,0.05)]">
                <p className="text-sm font-semibold text-slate-900">Profitability Engine</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  These values power break-even/minute, treatment pricing, ROI, and capital recovery reports.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );

  const renderLockedSetupLayout = (lockedFinancePanel) => (
    <div className="relative min-h-full overflow-hidden bg-[#F7F9FC] p-4 md:p-10">
      <div className="pointer-events-none absolute left-[8%] top-[-8rem] h-80 w-80 rounded-full bg-blue-100/35 blur-3xl" />
      <div className="pointer-events-none absolute right-[7%] top-32 h-72 w-72 rounded-full bg-cyan-100/20 blur-3xl" />
      <div className="mx-auto max-w-7xl">
        <div className="animate-setup-fade-up relative mb-8 overflow-hidden rounded-[30px] border border-slate-200/90 bg-white/95 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur-xl">
          <div className="relative p-6 md:p-8">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                  <Stethoscope className="h-4 w-4" />
                  Clinic foundation
                </div>
                <h1 className="mt-4 bg-gradient-to-r from-blue-800 via-blue-600 to-slate-800 bg-clip-text text-4xl font-semibold tracking-tight text-transparent drop-shadow-[0_10px_22px_rgba(37,99,235,0.12)] md:text-5xl">
                  Setup Your Clinic
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
                  Just three quick steps and your clinic is ready to run.
                </p>
              </div>
              {renderSetupJourneyThread()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_16px_42px_rgba(15,23,42,0.05)] md:p-8">
            <SectionHeader
              icon={LockKeyhole}
              title="Finance access required"
              description="Setup contains salary, cost, tax, depreciation, and investment values. Unlock Finance Lock to continue editing safely."
            />
            <p className="text-sm leading-6 text-slate-500">
              Your setup form will load after finance access is confirmed. This keeps sensitive clinic financial values protected while preserving tenant isolation.
            </p>
          </section>

          <aside className="xl:sticky xl:top-8 xl:self-start">
            <div className="space-y-5">
              <div className="overflow-hidden rounded-[28px] border border-blue-100/80 bg-white/95 shadow-[0_18px_48px_rgba(15,23,42,0.08)] backdrop-blur">
                <div className="relative overflow-hidden border-b border-blue-100/80 bg-[linear-gradient(135deg,rgba(37,99,235,0.16),rgba(226,232,240,0.5)_48%,rgba(219,234,254,0.42))] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">
                  <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-blue-300/25 blur-2xl" />
                  <div className="absolute -bottom-10 left-8 h-24 w-24 rounded-full bg-cyan-200/25 blur-2xl" />
                  <div className="relative">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-800">Live setup summary</p>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">Setup Dektal 360</h2>
                    <p className="mt-1 text-sm text-slate-600">Finance Lock protects this setup session.</p>
                  </div>
                </div>
                <div className="space-y-4 p-5">
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                    <span className="text-sm font-semibold text-slate-500">Setup completion</span>
                    <span className="text-lg font-semibold text-slate-900">Locked</span>
                  </div>
                  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                    <span className="text-sm font-semibold text-slate-500">Monthly overhead</span>
                    <span className="text-lg font-semibold text-slate-900">Protected</span>
                  </div>
                </div>
              </div>

              {lockedFinancePanel}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );

  return (
    <FinanceLockGate
      title="Finance Lock enabled. Enter PIN to continue."
      description="Setup contains cost, salary, tax, depreciation, and investment values."
      onAccessGranted={() => setFinanceReady(true)}
      renderLockedLayout={renderLockedSetupLayout}
    >
      {(financeStatus) => renderSetupPage(financeStatus)}
    </FinanceLockGate>
  );
}
